import { cn } from '@/shared/lib/utils/classnames';
import type { KeyValueBlock as KeyValueBlockType } from '@/shared/types/blocks';

type KeyValueBlockProps = Omit<KeyValueBlockType, 'type' | 'id'>;

/**
 * KeyValueBlock - 키-값 쌍 표시
 *
 * Figma 구조:
 * - Container: flex-col gap-[16px]
 * - 각 행: flex gap-[16px] (2개 아이템)
 * - 각 아이템: flex-1 flex-col gap-[6px]
 * - Key: 14px bold black
 * - Value: 16px normal #67696a (링크는 #3399ff)
 */
export function KeyValueBlock({ pairs, layout = 'vertical' }: KeyValueBlockProps) {
  // grid 레이아웃: 2개씩 행으로 묶음
  if (layout === 'grid') {
    // pairs를 2개씩 그룹화
    const rows: (typeof pairs)[] = [];
    for (let i = 0; i < pairs.length; i += 2) {
      rows.push(pairs.slice(i, i + 2));
    }

    return (
      <dl className="flex flex-col gap-[16px]">
        {rows.map((rowPairs) => (
          <div key={`row-${rowPairs[0]?.key}`} className="flex gap-[16px] w-full">
            {rowPairs.map((pair, index) => (
              <div key={`${pair.key}-${index}`} className="flex-1 flex flex-col gap-[6px] min-w-0">
                <dt className="text-[14px] font-bold text-black leading-[1.35]">{pair.key}</dt>
                <dd className="text-[16px] font-normal leading-[1.5]">
                  {pair.link ? (
                    <a
                      href={pair.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#3399ff] hover:underline cursor-pointer"
                    >
                      {pair.value}
                    </a>
                  ) : (
                    <span className="text-[#67696a]">{pair.value}</span>
                  )}
                </dd>
              </div>
            ))}
            {/* 홀수 개일 때 빈 공간 유지 */}
            {rowPairs.length === 1 && <div className="flex-1" />}
          </div>
        ))}
      </dl>
    );
  }

  // vertical/horizontal 레이아웃
  const layoutClass = {
    vertical: 'flex flex-col gap-4',
    horizontal: 'flex flex-row flex-wrap gap-x-6 gap-y-4',
  }[layout];

  return (
    <dl className={cn(layoutClass)}>
      {pairs.map((pair, index) => (
        <div
          key={`${pair.key}-${index}`}
          className={cn(
            layout === 'horizontal'
              ? 'flex flex-row items-baseline gap-2'
              : 'flex flex-col gap-[6px]',
          )}
        >
          <dt className="text-[14px] font-bold text-black leading-[1.35]">{pair.key}</dt>
          <dd className="text-[16px] font-normal leading-[1.5]">
            {pair.link ? (
              <a
                href={pair.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#3399ff] hover:underline cursor-pointer"
              >
                {pair.value}
              </a>
            ) : (
              <span className="text-[#67696a]">{pair.value}</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
